import { useEffect, useState } from 'react';
import { FormControl, FormLabel } from '@mui/material';

import useDebounce from 'Lib/hooks/useDebounce';
import useNotify from 'Lib/hooks/useNotify';
import AsyncSelect from 'Lib/muiComponents/common/AsyncSelect';
import isEmpty from 'Lib/utilities/isEmpty';
import unique from 'Lib/utilities/unique';

export default function AsyncFilter({ fetch, optionPath, value, createOption, params, label, getDefaultOptions, ...rest }) {
	const [loading, setLoading] = useState(true);
	const [options, setOptions] = useState([]);
	const [inputValue, setInputValue] = useState('');
	const [selectedOptions, setSelectedOptions] = useState([]);
	const debouncedValue = useDebounce(inputValue, 500);

	const { toastError } = useNotify();

	useEffect(() => {
		setLoading(false);
	}, []);

	useEffect(() => {
		if (value) {
			setLoading(true);

			if (getDefaultOptions) {
				getDefaultOptions(value)
					.then(defaultOptions => {
						setSelectedOptions(rest.multiple ? defaultOptions : defaultOptions[0]);
						setOptions(defaultOptions);
					})
					.finally(() => setLoading(false));
			} else {
				fetch(params)
					.then(response => {
						if (rest.multiple || rest.isMulti) {
							setSelectedOptions(
								response.data.map(option => {
									if (option.label && option.value) return option;

									return createOption(option);
								})
							);
						} else {
							if (response.data.label && response.data.value) return setSelectedOptions(response.data)[0];

							setSelectedOptions(createOption(response.data[0]));
						}
					})
					.catch(toastError)
					.finally(() => setLoading(false));
			}
		} else if (rest.options) {
			const list = options.map(option => createOption(option.contact));

			setOptions(list);
		}
	}, []);

	useEffect(() => {
		if (!isEmpty(debouncedValue)) {
			setLoading(true);

			fetch({ search: debouncedValue }).then(response => {
				const uniqueOptions =
					unique([...(Array.isArray(selectedOptions) ? selectedOptions : []), ...(Array.isArray(response.data) ? response.data : [])], optionPath) ?? [];

				if (createOption) {
					setOptions(
						uniqueOptions.map(option => {
							if (option.label && option.value) return option;

							return createOption(option);
						})
					);
				} else setOptions(uniqueOptions);

				setLoading(false);
			});
		}
	}, [debouncedValue]);

	return (
		<FormControl fullWidth>
			{label ? (
				<FormLabel id={`${label}-filter`} variant="list-filter">
					{label}
				</FormLabel>
			) : null}
			<AsyncSelect
				{...rest}
				multiple={rest.multiple}
				value={selectedOptions}
				limitTags={1}
				options={options}
				loading={loading}
				getOptionLabel={option => option?.label}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				onTextChange={event => setInputValue(event.target.value)}
				onChange={(event, val) => {
					setSelectedOptions(val);
					rest.onChange(event, val);
				}}
			/>
		</FormControl>
	);
}
